import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

export type ConfirmVariant = 'danger' | 'warning' | 'primary';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onClose: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
  icon?: IconDefinition;
}

const VARIANT_STYLES: Record<ConfirmVariant, { bg: string; iconColor: string; button: string }> = {
  danger:  { bg: '#fee2e2', iconColor: '#dc2626', button: '#ef4444' },
  warning: { bg: '#fef3c7', iconColor: '#d97706', button: '#f59e0b' },
  primary: { bg: '#dbeafe', iconColor: '#2563eb', button: '#3b82f6' },
};

const DEFAULT_ICON: Record<ConfirmVariant, IconDefinition> = {
  danger: faTrashAlt,
  warning: faExclamationTriangle,
  primary: faExclamationTriangle,
};

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  onConfirm,
  onClose,
  confirmLabel,
  cancelLabel,
  variant = 'danger',
  icon,
}) => {
  const { t } = useTranslation('common');

  if (!open) return null;

  const styles = VARIANT_STYLES[variant];
  const displayIcon = icon || DEFAULT_ICON[variant];

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 10001,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff', borderRadius: 16, padding: '24px 28px',
          width: '100%', maxWidth: 420, boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%', background: styles.bg,
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: styles.iconColor,
          }}>
            <FontAwesomeIcon icon={displayIcon} />
          </div>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: '#1e293b' }}>{title}</h3>
        </div>
        <p style={{ margin: '0 0 24px', fontSize: 14, color: '#64748b', lineHeight: 1.5 }}>{message}</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 18px', borderRadius: 8, border: '1px solid #e2e8f0',
              background: '#fff', color: '#475569', cursor: 'pointer',
              fontWeight: 500, fontSize: 14,
            }}
          >
            {cancelLabel ?? t('buttons.cancel')}
          </button>
          <button
            onClick={() => { onConfirm(); onClose(); }}
            style={{
              padding: '8px 18px', borderRadius: 8, border: 'none',
              background: styles.button, color: '#fff', cursor: 'pointer',
              fontWeight: 500, fontSize: 14,
            }}
          >
            {confirmLabel ?? t('buttons.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
